/**
 * Hasat Tahmin 2.0 — Feature 7
 *
 * Birikimli Derece-Gün (Cumulative Degree-Day) modeli.
 * Sıcaklık bağımlı büyüme hızını hesaplar.
 * Statik "5-10 gün" yerine gerçek çevre koşullarına dayalı tahmin.
 *
 * Formül: GDD = Σ max(0, T_avg - T_base)
 * Her tür için hedef GDD eşiği tanımlıdır.
 */

import { useMemo } from 'react';
import { useAppStore } from '../store';
export interface HarvestPrediction {
  bagId: string;
  bagName: string;
  speciesId: string;
  daysLeft: number;
  readyDate: Date;
  status: 'overdue' | 'ready' | 'soon' | 'upcoming' | 'early';
  confidence: 'high' | 'medium' | 'low';
  expectedDays: number;
  daysIn: number;
}
import { SPECIES_FRUITING_WINDOW, SPECIES_PIN_WINDOW } from '../services/thresholds';

// ── Degree-Day Parameters ─────────────────────────────────────────────────────

interface GDDParams {
  baseTemp: number;      // °C — below this, no growth
  targetGDD: number;     // cumulative degree-days to harvest
  optimalTemp: number;   // °C — peak growth rate
  maxTemp: number;       // °C — above this, growth stops
}

const SPECIES_GDD: Record<string, GDDParams> = {
  'lion-mane':   { baseTemp: 10, targetGDD: 90,  optimalTemp: 18, maxTemp: 24 },
  'shiitake':    { baseTemp: 8,  targetGDD: 120, optimalTemp: 16, maxTemp: 22 },
  'reishi':      { baseTemp: 15, targetGDD: 200, optimalTemp: 25, maxTemp: 32 },
  'turkey-tail': { baseTemp: 12, targetGDD: 150, optimalTemp: 21, maxTemp: 28 },
  'istiridye':   { baseTemp: 8,  targetGDD: 60,  optimalTemp: 18, maxTemp: 26 },
  'karides':     { baseTemp: 12, targetGDD: 50,  optimalTemp: 24, maxTemp: 30 },
};

// ── GDD Calculation ───────────────────────────────────────────────────────────

/**
 * Calculate effective degree-days for a single temperature reading.
 * Uses a triangular model: growth rate peaks at optimalTemp, zero at base and max.
 */
function calcEffectiveGDD(temp: number, params: GDDParams): number {
  if (temp <= params.baseTemp || temp >= params.maxTemp) return 0;

  if (temp <= params.optimalTemp) {
    // Linear increase from base to optimal
    return (temp - params.baseTemp) / (params.optimalTemp - params.baseTemp);
  } else {
    // Linear decrease from optimal to max
    return (params.maxTemp - temp) / (params.maxTemp - params.optimalTemp);
  }
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export interface HarvestPredictionV2 extends HarvestPrediction {
  accumulatedGDD: number;
  targetGDD: number;
  gddProgress: number;   // 0-1
  modelType: 'degree-day' | 'static';
  avgTempDuringFruiting?: number;
  pinDetectedDate?: string; // when set, harvest window is calculated from pin date
}

export function useHarvestPredictionsV2(): HarvestPredictionV2[] {
  const growBags = useAppStore(s => s.growBags);
  const readings = useAppStore(s => s.readings);

  return useMemo(() => {
    const now = Date.now();

    return growBags
      .filter(bag => bag.status === 'fruiting' && bag.fruitingStartDate)
      .map(bag => {
        // If pin date logged, use it as the reference — tighter and more accurate window
        const referenceDate = bag.pinDetectedDate ?? bag.fruitingStartDate!;
        const fruitingStart = new Date(bag.fruitingStartDate!).getTime();
        const referenceStart = new Date(referenceDate).getTime();
        const daysIn = (now - referenceStart) / 86400000;
        const gddParams = SPECIES_GDD[bag.speciesId];
        // Use pin-based window when available, fall back to fruiting window
        const pinWindow = SPECIES_PIN_WINDOW[bag.speciesId];
        const speciesWindow = (bag.pinDetectedDate && pinWindow)
          ? pinWindow
          : (SPECIES_FRUITING_WINDOW[bag.speciesId] ?? SPECIES_FRUITING_WINDOW['lion-mane']);

        // Filter readings since reference start (pin date or fruiting start)
        const fruitingReadings = readings.filter(r => {
          const ts = new Date(r.timestamp).getTime();
          return ts >= fruitingStart && ts <= now; // GDD always from fruiting start for energy accumulation
        });

        let accumulatedGDD = 0;
        let avgTempDuringFruiting: number | undefined;
        let modelType: HarvestPredictionV2['modelType'] = 'static';

        if (gddParams && fruitingReadings.length >= 3) {
          modelType = 'degree-day';

          // Group readings by day and calculate daily GDD
          const byDay: Record<string, number[]> = {};
          fruitingReadings.forEach(r => {
            const day = r.timestamp.slice(0, 10);
            if (!byDay[day]) byDay[day] = [];
            byDay[day].push(r.temperature);
          });

          for (const temps of Object.values(byDay)) {
            const avgTemp = temps.reduce((s, t) => s + t, 0) / temps.length;
            accumulatedGDD += calcEffectiveGDD(avgTemp, gddParams) * gddParams.targetGDD / speciesWindow.idealDays;
          }

          const allTemps = fruitingReadings.map(r => r.temperature);
          avgTempDuringFruiting = Math.round(allTemps.reduce((s, t) => s + t, 0) / allTemps.length * 10) / 10;
        }

        // Calculate expected days and days left
        let expectedDays: number;
        let daysLeft: number;

        if (modelType === 'degree-day' && gddParams && accumulatedGDD > 0) {
          // Estimate remaining GDD needed
          const remainingGDD = Math.max(0, gddParams.targetGDD - accumulatedGDD);
          // Use recent average temp to project remaining days
          const recentReadings = fruitingReadings.slice(-12);
          const recentAvgTemp = recentReadings.length > 0
            ? recentReadings.reduce((s, r) => s + r.temperature, 0) / recentReadings.length
            : 18;
          const dailyGDDRate = calcEffectiveGDD(recentAvgTemp, gddParams) * gddParams.targetGDD / speciesWindow.idealDays;
          daysLeft = dailyGDDRate > 0 ? Math.round(remainingGDD / dailyGDDRate) : speciesWindow.idealDays - Math.round(daysIn);
          expectedDays = Math.round(daysIn) + daysLeft;
        } else {
          // Fallback to static model
          const bagHistoryAvg = bag.flushHarvests?.length > 0
            ? bag.flushHarvests.reduce((sum, f) => {
                const harvestMs = new Date(f.harvestDate).getTime();
                return sum + (harvestMs - fruitingStart) / 86400000;
              }, 0) / bag.flushHarvests.length
            : null;

          expectedDays = bagHistoryAvg !== null
            ? speciesWindow.idealDays * 0.7 + bagHistoryAvg * 0.3
            : speciesWindow.idealDays;
          daysLeft = Math.round(expectedDays - daysIn);
        }

        const readyDate = new Date(now + daysLeft * 86400000);

        let status: HarvestPrediction['status'];
        if (daysLeft < 0) status = 'overdue';
        else if (daysLeft === 0) status = 'ready';
        else if (daysLeft <= 2) status = 'soon';
        else if (daysLeft <= 5) status = 'upcoming';
        else status = 'early';

        const completedFlushes = bag.flushHarvests?.filter(f => f.harvestDate) ?? [];
        let confidence: HarvestPrediction['confidence'];
        if (modelType === 'degree-day' && fruitingReadings.length >= 12) confidence = 'high';
        else if (completedFlushes.length >= 1) confidence = 'medium';
        else confidence = 'low';

        const gddProgress = gddParams
          ? Math.min(1, accumulatedGDD / gddParams.targetGDD)
          : Math.min(1, daysIn / speciesWindow.idealDays);

        return {
          bagId: bag.id,
          bagName: bag.name,
          speciesId: bag.speciesId,
          daysLeft,
          readyDate,
          status,
          confidence,
          expectedDays,
          daysIn,
          accumulatedGDD,
          targetGDD: gddParams?.targetGDD ?? 0,
          gddProgress,
          modelType,
          avgTempDuringFruiting,
          pinDetectedDate: bag.pinDetectedDate,
        };
      });
  }, [growBags, readings]);
}
